import { cn } from "../utils";
import type { LucideIcon } from "lucide-react";

export type BadgeColor =
  | "default"
  | "red"
  | "yellow"
  | "green"
  | "blue"
  | "indigo"
  | "purple"
  | "pink";

export type BadgeSize = "sm" | "md";

export type BadgeProps = {
  label: string;
  color?: BadgeColor;
  size?: BadgeSize;
  iconLeft?: LucideIcon;
  iconRight?: LucideIcon;
  className?: string;
};

export function Badge({
  label,
  color = "default",
  size = "md",
  iconLeft: IconLeft,
  iconRight: IconRight,
  className,
}: BadgeProps) {
  const colorStyles: Record<BadgeColor, string> = {
    default: "bg-gray-50 text-gray-500/90 ring-gray-500/10",
    red: "bg-red-50 text-red-700 ring-red-600/10",
    yellow: "bg-yellow-50 text-yellow-800 ring-yellow-600/20",
    green: "bg-green-50 text-green-700 ring-green-600/20",
    blue: "bg-blue-50 text-blue-700 ring-blue-700/10",
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-700/10",
    purple: "bg-default-50 text-default-700 ring-default-700/10",
    pink: "bg-pink-50 text-pink-700 ring-pink-700/10",
  };

  const sizeStyles: Record<BadgeSize, string> = {
    sm: "text-xs px-2 py-0.5 gap-x-1 [&_svg]:size-3",
    md: "text-sm px-2.5 py-1 gap-x-1.5 [&_svg]:size-4",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium ring-1 ring-inset truncate min-w-0",
        colorStyles[color],
        sizeStyles[size],
        className
      )}
    >
      {IconLeft && <IconLeft aria-hidden="true" />}
      <span className="truncate">{label}</span>
      {IconRight && <IconRight aria-hidden="true" />}
    </span>
  );
}
